import { memo } from 'react';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '@/shared/shadcn/lib/utils';
import { useSidebarStore } from '@/shared/components/layout/_useSidebarStore';
import { hasActiveDescendant, isItemActive, matchesActivePathPrefix } from '@/shared/components/layout/_activePath';

import type { NavigateFunction } from 'react-router-dom';
import type { SidebarMenuItem } from '@/shared/components/layout/types';

interface SidebarNavItemProps {
  menuItem: SidebarMenuItem;
  /**
   * 현재 경로. 행마다 useLocation()을 부르면 쿼리 파라미터만 바뀌어도 트리 전체가 리렌더된다
   * (목록 화면의 필터·페이지네이션이 전부 쿼리 파라미터라 매우 잦다). 상위에서 한 번만 구독해 내려준다.
   */
  pathname: string;
  /** 상위에서 내려받는 navigate — 행마다 useNavigate()를 부르면 memo가 무력화된다 */
  navigate: NavigateFunction;
  /** 중첩 깊이 (0 = 최상위) */
  depth?: number;
  /** 조상들의 id를 이어붙인 펼침 키 — 다른 depth의 동일 id끼리 상태가 섞이지 않게 함 */
  parentKey?: string;
  /** 부모가 lazy 리스트(수업 목록 등)면 자식을 불릿 목록으로 렌더한다 */
  bulleted?: boolean;
  /** 조상이 disabled면 자손도 흐리게 */
  inheritedDisabled?: boolean;
  className?: string;
}

/** 최상위 라벨이 시작하는 x = mx-3(0.75) + px-3(0.75) + 아이콘(1.5) + gap-3(0.75). 하위 항목을 여기에 맞춰 들여쓴다 */
const CHILD_INDENT = 'pl-[3.75rem]';

const MobileSidebarNavItem = memo(function MobileSidebarNavItem({
  menuItem,
  pathname,
  navigate,
  depth = 0,
  parentKey,
  bulleted = false,
  inheritedDisabled = false,
  className,
}: SidebarNavItemProps) {
  const isChild = depth > 0;
  const expandKey = parentKey ? `${parentKey}/${menuItem.id}` : menuItem.id;

  // store 직접 구독 — 부모 리렌더에서 격리
  const isExpanded = useSidebarStore((s) => s.expandedMenuKeys.includes(expandKey));
  const toggleExpandedMenuKey = useSidebarStore((s) => s.toggleExpandedMenuKey);

  // 클릭으로 펼침/접힘을 토글하는 항목: API로 채우는 lazy 메뉴이거나, 정적 children을 토글만 하는 expandable 메뉴
  const isExpandable = !!(menuItem.lazyExpandChildren || menuItem.expandable);
  const childrenList = menuItem.children;
  const hasChildren = !!childrenList?.length;
  const showChevron = isExpandable || hasChildren;

  const disabled = inheritedDisabled || !!menuItem.disabled;
  const isActive = !disabled && isItemActive(menuItem, pathname);
  // 손자 이하가 활성인 경우에도 조상 행을 활성 표시해야 하므로 재귀로 탐색
  const isBranchActive = !disabled && (hasActiveDescendant(childrenList, pathname) || matchesActivePathPrefix(menuItem, pathname));

  /** 클릭해도 아무 일이 없는 행 (경로 미정 + 펼칠 것도 없음, 또는 비활성) */
  const isInert = disabled || (!menuItem.path && !isExpandable);

  const handleClick = () => {
    if (disabled) return;
    if (isExpandable) {
      toggleExpandedMenuKey(expandKey);
      return;
    }
    if (menuItem.path) navigate(menuItem.path);
  };

  // expandable은 펼쳤을 때만, 그 외(정적 children)는 항상 열어둔다
  const isChildPanelOpen = hasChildren && (!isExpandable || isExpanded);

  // --- 행 스타일 ---
  const rowClass = isChild
    ? bulleted
      ? cn(
          'flex min-h-9 items-start gap-2 py-2 pl-6 pr-4 text-[14px] leading-snug transition-colors',
          disabled ? 'text-blue-gray-300' : isActive ? 'font-medium text-support-blue' : 'text-blue-gray-600',
          // 로딩·에러·"수업 없음" 안내 행 (path 없음)은 흐리게
          !disabled && isInert && 'text-blue-gray-400 italic',
          !isInert && 'cursor-pointer hover:text-blue-gray-900'
        )
      : cn(
          `flex h-10 items-center ${CHILD_INDENT} pr-3 text-[14px] transition-colors`,
          disabled ? 'text-blue-gray-300' : isActive ? 'font-medium text-support-blue' : 'text-blue-gray-600',
          !isInert && 'cursor-pointer hover:text-blue-gray-900'
        )
    : cn(
        'mx-3 flex h-12 items-center gap-3 rounded-xl px-3 text-[15px] font-bold transition-colors',
        !isInert && 'active:scale-[0.98]',
        disabled ? 'text-blue-gray-300' : isActive || isBranchActive ? 'bg-gray-300 text-blue-gray-900' : 'text-blue-gray-900 hover:bg-gray-200',
        isInert ? 'cursor-default' : 'cursor-pointer'
      );

  const iconColor = disabled ? 'text-blue-gray-300' : 'text-blue-gray-900';
  const chevronColor = disabled ? 'text-blue-gray-300' : 'text-blue-gray-500';

  return (
    <>
      <button
        type='button'
        aria-current={isActive ? 'page' : undefined}
        aria-expanded={isExpandable ? isExpanded : undefined}
        aria-disabled={isInert || undefined}
        disabled={disabled}
        className={cn(
          'transition-transform duration-100 ease-out select-none [-webkit-tap-highlight-color:transparent]',
          rowClass,
          menuItem.className,
          className
        )}
        onClick={handleClick}>
        {isChild && bulleted && !isInert && <span className='text-blue-gray-400 mt-[3px] shrink-0'>•</span>}

        {menuItem.Icon && <i className={cn('tw-icon-wrap size-6 shrink-0', iconColor)}>{menuItem.Icon}</i>}

        <span className={cn('min-w-0 flex-1 text-left', !bulleted && 'truncate')}>
          {menuItem.title}
          {menuItem.subtitle && (
            <span className={cn('ml-1 text-[13px] font-normal', disabled ? 'text-blue-gray-300' : 'text-blue-gray-500')}>{menuItem.subtitle}</span>
          )}
        </span>

        {showChevron && <ChevronDownIcon className={cn('size-4 shrink-0 transition-transform', chevronColor, isExpandable && isExpanded && 'rotate-180')} />}
      </button>

      {disabled && !isChild && menuItem.disabledReason && (
        <p className={cn('text-blue-gray-400 -mt-1 mb-1 text-[12px]', CHILD_INDENT)}>{menuItem.disabledReason}</p>
      )}

      {/* 펼침/접힘 — grid-rows 트릭으로 높이를 부드럽게 전환한다 (JS 애니메이션 프레임 고착 회피) */}
      {hasChildren && (
        <div className={cn('grid transition-[grid-template-rows] duration-200 ease-out', isChildPanelOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
          <div className='min-h-0 overflow-hidden'>
            {childrenList?.map((childMenuItem) => (
              <MobileSidebarNavItem
                key={childMenuItem.id}
                menuItem={childMenuItem}
                pathname={pathname}
                navigate={navigate}
                depth={depth + 1}
                parentKey={expandKey}
                bulleted={!!menuItem.lazyExpandChildren}
                inheritedDisabled={disabled}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
});

export default MobileSidebarNavItem;
